import { apiService } from './apiService';

export interface UploadV2File {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'failed';
  progress: number;
  uploaded: boolean;
  processed: boolean;
  error?: string | null;
}

export interface UploadV2Session {
  sessionId: string;
  albumId: string;
  status: 'active' | 'paused' | 'completed' | 'failed';
  createdAt: string;
  updatedAt: string;
  totalFiles: number;
  processedFiles: number;
  failedFiles: number;
  pendingFiles: number;
}

export interface UploadV2InitiateRequest {
  files: Array<{
    fileId: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    fileSignature?: string; // Magic bytes for backend validation (security)
    fileData?: string; // Base64 SVG content for sanitization (only for SVG files)
  }>;
  settings: {
    maxTitleLength: number;
    maxDescriptionLength: number;
    maxKeywords: number;
  };
  customInstruction?: string;
  sendEmailNotification?: boolean; // Send email when album processing is complete
}

export interface UploadV2AlbumCreationRequest {
  files: Array<{
    fileName: string;
    fileSize: number;
    mimeType: string;
  }>;
  settings: {
    maxTitleLength: number;
    maxDescriptionLength: number;
    maxKeywords: number;
  };
  customInstruction?: string;
}

export interface UploadV2AlbumCreationResponse {
  success: boolean;
  message: string;
  albumId?: string;
  albumSlug?: string;
  redirectUrl?: string;
  partialSuccess?: boolean;
  requiresCredits?: boolean;
  errorCode?: string;
}

export interface UploadV2InitiateResponse {
  success: boolean;
  sessionId: string;
  albumSlug?: string;
  uploads: Array<{
    fileId: string;
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    expiresIn: number;
  }>;
  message?: string;
  totalFiles?: number;
}

export interface UploadV2ProgressResponse {
  sessionId: string;
  status: string;
  progress: number;
  files: Array<{
    fileId: string;
    status: string;
    progress: number;
    error?: string;
  }>;
  stats: {
    total: number;
    processed: number;
    failed: number;
    pending: number;
  };
}

class UploadV2ApiService {

  /**
   * Create album for Upload V2
   */
  async createAlbum(request: UploadV2AlbumCreationRequest): Promise<UploadV2AlbumCreationResponse> {
    try {
      // Get auth token (same as V1)
      const { AuthCore } = await import('../core/AuthCore');
      const authCore = AuthCore.getInstance();
      const token = await authCore.getAccessToken();

      if (!token) {
        throw new Error('Authentication required');
      }

      // Use V1 endpoint for album creation (V2 uses same endpoint)
      const response = await apiService.makeTestRequest<UploadV2AlbumCreationResponse>(
        '/upload/create-album',
        {
          method: 'POST',
          body: JSON.stringify(request),
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Return the data from the response (backend wraps data in .data property)
      return response.data;
    } catch (error) {
      console.error('Failed to create album:', error);
      throw error;
    }
  }

  /**
   * Get album data from database
   */
  async getAlbumData(albumSlug: string): Promise<{
    albumId: number;
    albumSlug: string;
    albumName: string;
    createdAt: string;
    userId: string;
  }> {
    try {
      // Get auth token (same as V1)
      const { AuthCore } = await import('../core/AuthCore');
      const authCore = AuthCore.getInstance();
      const token = await authCore.getAccessToken();

      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await apiService.makeTestRequest<{
        album: {
          id: number;
          album_slug: string;
          title: string;
          created_at: string;
          user_id: string;
        };
      }>(`/albums/${albumSlug}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Transform nested album object to flat structure expected by frontend
      const album = response.data.album;
      return {
        albumId: album.id,
        albumSlug: album.album_slug,
        albumName: album.title, // Map 'title' to 'albumName'
        createdAt: album.created_at,
        userId: album.user_id
      };
    } catch (error) {
      console.error('Failed to get album data:', error);
      throw error;
    }
  }

  /**
   * Update album title
   */
  async updateAlbumTitle(albumSlug: string, newTitle: string): Promise<void> {
    try {
      // Get auth token (same as V1)
      const { AuthCore } = await import('../core/AuthCore');
      const authCore = AuthCore.getInstance();
      const token = await authCore.getAccessToken();

      if (!token) {
        throw new Error('Authentication required');
      }

      await apiService.makeTestRequest<{
        success: boolean;
        message: string;
      }>(
        `/albums/${albumSlug}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ title: newTitle }),
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Failed to update album title:', error);
      throw error;
    }
  }

  /**
   * Initiate Upload V2 session
   */
  async initiateUpload(request: UploadV2InitiateRequest): Promise<UploadV2InitiateResponse> {
    try {
      // Get auth token (same as V1)
      const { AuthCore } = await import('../core/AuthCore');
      const authCore = AuthCore.getInstance();
      const token = await authCore.getAccessToken();

      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await apiService.makeTestRequest<UploadV2InitiateResponse>(
        '/upload/initiate',
        {
          method: 'POST',
          body: JSON.stringify(request),
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Failed to initiate upload:', error);
      throw error;
    }
  }

  /**
   * Get upload session progress
   */
  async getProgress(sessionId: string): Promise<UploadV2ProgressResponse> {
    try {
      // Get auth token (same as V1)
      const { AuthCore } = await import('../core/AuthCore');
      const authCore = AuthCore.getInstance();
      const token = await authCore.getAccessToken();

      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await apiService.makeTestRequest<UploadV2ProgressResponse>(
        `/upload/progress?sessionId=${sessionId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Failed to get progress:', error);
      throw error;
    }
  }

  /**
   * Pause upload session
   */
  async pauseSession(sessionId: string): Promise<void> {
    try {
      await apiService.makeTestRequest(`/upload/session/${sessionId}/pause`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Failed to pause session:', error);
      throw error;
    }
  }

  /**
   * Resume upload session
   */
  async resumeSession(sessionId: string): Promise<void> {
    try {
      await apiService.makeTestRequest(`/upload/session/${sessionId}/resume`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Failed to resume session:', error);
      throw error;
    }
  }

  /**
   * Cancel upload session
   */
  async cancelSession(sessionId: string): Promise<void> {
    try {
      await apiService.makeTestRequest(`/upload/session/${sessionId}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Failed to cancel session:', error);
      throw error;
    }
  }

  /**
   * Confirm upload completion and trigger SQS processing
   */
  async confirmUpload(request: {
    sessionId: string;
    fileId: string;
    s3Key: string;
  }): Promise<{
    success: boolean;
    message: string;
    fileId: string;
    sessionId: string;
    sqsMessageId?: string;
  }> {
    try {
      const { AuthCore } = await import('../core/AuthCore');
      const authCore = AuthCore.getInstance();
      const token = await authCore.getAccessToken();

      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await apiService.makeTestRequest<{
        success: boolean;
        message: string;
        fileId: string;
        sessionId: string;
        sqsMessageId?: string;
      }>(
        '/upload/confirm-upload',
        {
          method: 'POST',
          body: JSON.stringify(request),
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Failed to confirm upload:', error);
      throw error;
    }
  }

  /**
   * Upload file to S3 using presigned URL
   */
  async uploadFileToS3(
    file: File, 
    uploadUrl: string, 
    _fields: Record<string, string>,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          console.error('S3 upload failed:', xhr.status, xhr.responseText);
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        console.error('S3 upload error:', file.name);
        reject(new Error('Upload failed due to network error'));
      });
      
      // Use PUT method for simple presigned URL
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      
      // Send file directly
      xhr.send(file);
    });
  }
}

export const uploadV2ApiService = new UploadV2ApiService();
